import * as React from 'react';
import { Link } from 'react-router-dom';
import { History, Location } from 'history';
import propz from 'propz';
import { AGE_GROUPS } from 'Src/consts/school';
import { GenericSummary } from 'Src/components/GenericSummary/GenericSummary';
import { Loader } from 'Src/components/Loader/Loader';
import {
  getSuperAdminSchool,
  updateSuperAdminSchool,
  SuperAdminSchoolUpdateData
} from 'Src/helpers/service/superadmin/schools';
import { SuperAdminSchoolStudents } from './Students/SuperAdminSchoolStudents';
import { SuperAdminSchoolForms } from './Forms/SuperAdminSchoolForms';
import { SuperAdminSchoolHouses } from './Houses/SuperAdminSchoolHouses';
import { SuperAdminSchoolEvents } from './Events/SuperAdminSchoolEvents';
import { SuperAdminSchoolTournaments } from './Tournaments/SuperAdminSchoolTournaments';
import { SuperAdminSchoolPayments } from './Payments/SuperAdminSchoolPayments';
import { SuperAdminSchoolSubscriptions } from './Subscriptions/SuperAdminSchoolSubscriptions';
import { SuperAdminSchoolNotifications } from './Notifications/SuperAdminSchoolNotifications';
import { SuperAdminSchoolActionDescriptors } from './ActionDescriptors/SuperAdminSchoolActionDescriptors';
import { SuperAdminSchoolModules } from './Modules/SuperAdminSchoolModules';
import { SuperAdminSchoolSports } from './Sports/SuperAdminSchoolSports';
import { SuperAdminSchoolAllowedSports } from './AllowedSports/SuperAdminSchoolAllowedSports';
import { SuperAdminSchoolExports } from './Exports/SuperAdminSchoolExports';
import { SuperAdminSchoolTemplates } from './Templates/SuperAdminSchoolTemplates';
import { SuperAdminSchoolUserStats } from './UserStats/SuperAdminSchoolUserStats';
import { SuperAdminStudentHistory } from './StudentHistory/SuperAdminStudentHistory';
import { GmEmailTemplateForm, GmEmailTemplateSettings } from 'Src/components/GmEmailTemplateForm/GmEmailTemplateForm';
import { getUploadedImageUrl, uploadSuperAdminImage } from 'Src/helpers/service/image';

const TABS = [
  'Overview',
  'Settings',
  'Registration',
  'Invite settings',
  'Public site',
  'Feature flags',
  'Students',
  'Forms',
  'Houses',
  'Events',
  'Tournaments',
  'Payments',
  'Subscriptions',
  'Modules',
  'Sports',
  'Allowed sports',
  'Exports',
  'Templates',
  'User stats',
  'Notifications',
  'Action descriptors',
  'GM Email Template',
  'Student history'
];

const SCHOOL_STATUS_OPTIONS = [
  { value: 'PUBLIC_AVAILABLE', text: 'Enabled' },
  { value: 'PROTECTED', text: 'Protected' },
  { value: 'DISABLED', text: 'Disabled' }
];

const SUBSCRIPTION_PLAN_OPTIONS = [
  { value: '', text: 'Not set' },
  { value: 'FULL', text: 'Full' },
  { value: 'LITE', text: 'Lite' },
  { value: 'TOURNAMENTS_PARTICIPATION', text: 'Tournaments participation' },
  { value: 'FREE_COMMUNICATION', text: 'Free communication' }
];

const AGE_GROUPS_NAMING_OPTIONS = [
  { value: '', text: 'Not set' },
  { value: 'ENGLISH', text: 'English' },
  { value: 'SCOTTISH', text: 'Scottish' },
  { value: 'U5-U18', text: 'U5-U18' },
  { value: 'US', text: 'US' }
];

const STUDENT_VIEW_OPTIONS = [
  { value: '', text: 'Not set' },
  { value: 'SHORTEN_LASTNAME_ONLY', text: 'Shorten last name only' },
  { value: 'SHORTEN_FIRSTNAME_AND_LASTNAME', text: 'Shorten first and last name' }
];

const EDU_PHASE_OPTIONS = [
  { value: '', text: 'Not set' },
  { value: 'PRIMARY', text: 'Primary' },
  { value: 'SECONDARY', text: 'Secondary' },
  { value: 'ALL_THROUGH', text: 'All-through' }
];

const GENDER_OPTIONS = [
  { value: '', text: 'Not set' },
  { value: 'MALE', text: 'Male' },
  { value: 'FEMALE', text: 'Female' }
];

const SCHOOL_GENDER_OPTIONS = [
  { value: '', text: 'Not set' },
  { value: 'MALE', text: 'Male' },
  { value: 'FEMALE', text: 'Female' },
  { value: 'MIXED', text: 'Mixed' }
];

const PHOTO_ACCESS_PRESET_OPTIONS = [
  { value: '', text: 'Not set' },
  { value: 'PRIVATE', text: 'Private' },
  { value: 'ALL_PARTICIPANT_PARENTS', text: 'Your school only' }
];

const CLUB_INVITATIONS_OPTIONS = [
  { value: '', text: 'Not set' },
  { value: 'STUDENT', text: 'Student' },
  { value: 'PARENT', text: 'Parent' }
];

const INVITE_ACTION_OPTIONS = [
  { value: 'MANUAL', text: 'Manual' },
  { value: 'AUTO_ACCEPT', text: 'Auto accept' }
];

const PERMISSION_PRESET_OPTIONS = [
  { value: 'ADMIN', text: 'Admin' },
  { value: 'MANAGER', text: 'Manager' },
  { value: 'TEACHER', text: 'Teacher' },
  { value: 'COACH', text: 'Coach' },
  { value: 'PARENT', text: 'Parent' },
  { value: 'STUDENT', text: 'Student' },
  { value: 'GOVERNOR', text: 'Governor' },
  { value: 'SLT', text: 'School leadership (SLT)' },
  { value: 'PUBLIC_BLOGGER', text: 'Public blogger' },
  { value: 'TOURNAMENT_FOLLOWER', text: 'Tournament follower' }
];

const AVAILABLE_AGE_OPTIONS = AGE_GROUPS.ENGLISH.map((text, index) => ({
  value: index,
  text
}));

const PARENT_AUTO_LINK_MATCH_MODE_OPTIONS = [
  { value: 'EMAIL_AND_PHONE', text: 'Email AND phone (both must match)' },
  { value: 'EMAIL_ONLY', text: 'Email only' },
  { value: 'PHONE_ONLY', text: 'Phone only' }
];

const PARENT_REGISTRATION_FIELD_STATUS_OPTIONS = [
  { value: 'REQUIRED', text: 'Required' },
  { value: 'OPTIONAL', text: 'Optional' },
  { value: 'HIDDEN', text: 'Hidden' }
];

const PARENT_REGISTRATION_FIELDS = [
  { key: 'childFirstName', label: 'Child first name' },
  { key: 'childLastName', label: 'Child last name' },
  { key: 'childDateOfBirth', label: 'Child date of birth' },
  { key: 'childGender', label: 'Child gender' },
  { key: 'childForm', label: 'Registration form / class' },
  { key: 'childHouse', label: 'House' }
];

const ALL_PARENT_RELATIONSHIPS: string[] = [
  'Agent',
  'Aunt',
  'Auntie',
  'BRO',
  'Babysitter',
  'Brother',
  'Brother, half',
  'Brother, natural/adoptive',
  'Carer',
  'Chauffeur',
  'ChildMinder',
  'Childcare Officer',
  'Childminder',
  'Contact Person',
  'Court appointed guardian',
  'Cousin',
  'Doctor',
  'Driver',
  'Emergency',
  'FFR',
  'Family Friend',
  'Family member',
  'Father',
  "Father's significant other",
  'Father, foster',
  'Father, natural/adoptive',
  'Father, step',
  'Father_copy',
  'Foster Father',
  'Foster Mother',
  'Foster Parent',
  'Foster parent',
  'Friend',
  'GODMOTHER',
  'GRANDPARENT',
  'Godmother',
  'Grandfather',
  'Grandmother',
  'Grandparent',
  'Grandson',
  'Guardian',
  'Head Teacher',
  'ICE',
  'ICE - Brother',
  'ICE - Family Friend',
  'ICE - Friend',
  'ICE - Mother',
  'ICE - Sister',
  'ICE - Uncle',
  'Mother',
  "Mother's significant other",
  'Mother, foster',
  'Mother, natural/adoptive',
  'Mother, step',
  'Mother_copy',
  'Nanny',
  'Nanny/housekeeper',
  'Neighbour',
  'Not known',
  'Other',
  'Other Contact',
  'Other Family',
  'Other Family Member',
  'Other Non-Family Contact',
  'Other Relative',
  'PA to Parent',
  'Parents partner',
  'Parents_copy',
  'Partner',
  'Partner of parent',
  'SIS',
  'Sister',
  'Sister, natural/adoptive',
  'Social Worker',
  'Special Contact',
  'Step Father',
  'Step Mother',
  'Step Parent',
  'Stepfather',
  'Stepmother',
  'Uncle',
  'Unknown'
];

interface Props {
  match: {
    params: {
      schoolId: string;
    };
  };
  history: History;
  location: Location;
}

interface SchoolForm {
  [key: string]: any;
  name: string;
  pic: string;
  description: string;
  status: string;
  domain: string;
  email: string;
  notificationEmail: string;
  sportsDepartmentEmail: string;
  phone: string;
  address: string;
  subscriptionPlan: string;
  ageGroupsNaming: string;
  publicStudentViewType: string;
  eduPhase: string;
  gender: string;
  availableGender: string;
  availableAges: number[];
  allowedPermissionPresets: { [key: string]: boolean };
  additionalPermissionRequestFields: { [key: string]: 'HIDDEN' | 'OPTIONAL' | 'REQUIRED' };
  onEventInviteAction: string;
  onTournamentInviteAction: string;
  onTournamentEventInviteAction: string;
  onInviteEmailNotification: boolean;
  photoAccessPreset: string;
  clubInvitations: string;
  shortCode: string;
  county: string;
  twitterAccountName: string;
  applicationFeePercentage: string;
  settingsCommentsEnabled: boolean;
  settingsPhotosEnabled: boolean;
  settingsAvailabilityStudentEnabled: boolean;
  settingsAvailabilityParentEnabled: boolean;
  studentImportForAdminAllowed: boolean;
  availableForRegistration: boolean;
  studentSelfRegistrationEnabled: boolean;
  notificationsEnabled: boolean;
  allowedForFollowing: boolean;
  eventPerformanceEnabledForParent: boolean;
  eventCommunicationsAvailable: boolean;
  tournamentCommunicationsAvailable: boolean;
  isClubsEnabled: boolean;
  isClubPriorityEnabled: boolean;
  isAbsenceRequestEnabled: boolean;
  isPaymentsEnabled: boolean;
  canPublishWebSite: boolean;
  canAcceptStaffRoles: boolean;
  isFavoriteSportsEnabled: boolean;
  canCreateStudentFromParentPermissionRequest: boolean;
  isShowSchoolSelectOnPublicSite: boolean;
  isShowLeagueTableOnPublicSite: boolean;
  isShowSchoolTableOnPublicSite: boolean;
  isShowTournamentsOnPublicSite: boolean;
  isCreateSmsNotificationChannelForNewUsers: boolean;
  isSendSmsWithAllSystemEvents: boolean;
  isGeneralMessagesEnabled: boolean;
  isFirstAdminAutoAccept: boolean;
  isParentsAndStudentsAutoAccept: boolean;
  allowedParentRelationships: string[];
  parentAutoLinkMatchMode: string;
  isCoachSeenOnlyAffectedEvents: boolean;
  isTeacherSeenOnlyAffectedEvents: boolean;
  isOneOffCodeOnRegisterEnabled: boolean;
  isEventUpdateNotificationCustomMessageEnabled: boolean;
  isFollowersAutoenroll: boolean;
  canSellPhotos: boolean;
  isShowStudentEventClashes: boolean;
  isShowAggregatedHouseLeagueOnPublicWebsite: boolean;
  gmEmailHeaderColor: string;
  gmEmailHeaderEnabled: boolean;
  gmEmailHeaderLogoEnabled: boolean;
  gmEmailHeaderNameEnabled: boolean;
  gmEmailHeaderLogoAlignment: 'left' | 'center' | 'right';
  gmEmailHeaderNamePlacement: 'above' | 'left' | 'right' | 'below';
  gmEmailHeaderNameAlignment: 'left' | 'center' | 'right';
  gmEmailFooterHtml: string;
  gmEmailFooterBackgroundColor: string;
  gmEmailFooterTextColor: string;
  gmEmailFooterAlignment: 'left' | 'center' | 'right';
}

interface State {
  school: any;
  isLoading: boolean;
  isSaving: boolean;
  isLogoUploading: boolean;
  errorMessage: string;
  successMessage: string;
  currentTabIndex: number;
  isEditMode: boolean;
  form: SchoolForm;
}

function getValue(item: any, path: string[], fallback: string = '—'): string {
  const value = propz.get(item, path, '');

  if (value === null || typeof value === 'undefined' || value === '') {
    return fallback;
  }

  return String(value);
}

function getBoolValue(item: any, path: string[], fallback: boolean = false): boolean {
  const value = propz.get(item, path, fallback);
  return Boolean(value);
}

function getPostcode(school: any): string {
  return getValue(school, ['postcode', 'postcode']);
}

function getPublicSiteUrl(school: any): string {
  const domain = getValue(school, ['domain'], '');

  if (!domain) {
    return '—';
  }

  return `${domain}.squadintouch.com`;
}

function formatValue(value?: string): string {
  return value
    ? value
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, letter => letter.toUpperCase())
    : '—';
}

function formatBoolean(value: any): string {
  return value ? 'Yes' : 'No';
}

function getParentRegistrationFields(school: any): { [key: string]: 'HIDDEN' | 'OPTIONAL' | 'REQUIRED' } {
  const source = propz.get(school, ['additionalPermissionRequestFields'], {}) || {};

  return PARENT_REGISTRATION_FIELDS.reduce((result, field) => {
    const status = source[field.key];
    result[field.key] = status === 'HIDDEN' || status === 'REQUIRED' ? status : 'OPTIONAL';
    return result;
  }, {} as { [key: string]: 'HIDDEN' | 'OPTIONAL' | 'REQUIRED' });
}

function schoolToForm(school: any): SchoolForm {
  return {
    name: getValue(school, ['name'], ''),
    pic: getValue(school, ['pic'], ''),
    description: getValue(school, ['description'], ''),
    status: getValue(school, ['status'], ''),
    domain: getValue(school, ['domain'], ''),
    email: getValue(school, ['email'], ''),
    notificationEmail: getValue(school, ['notificationEmail'], ''),
    sportsDepartmentEmail: getValue(school, ['sportsDepartmentEmail'], ''),
    phone: getValue(school, ['phone'], ''),
    address: getValue(school, ['address'], ''),
    subscriptionPlan: getValue(school, ['subscriptionPlan'], ''),
    ageGroupsNaming: getValue(school, ['ageGroupsNaming'], ''),
    publicStudentViewType: getValue(school, ['publicStudentViewType'], ''),
    eduPhase: getValue(school, ['eduPhase'], ''),
    gender: getValue(school, ['gender'], ''),
    availableGender: getValue(school, ['availableGender'], ''),
    availableAges: Array.isArray(school.availableAges) ? school.availableAges : [],
    allowedPermissionPresets: school.allowedPermissionPresets || {},
    additionalPermissionRequestFields: getParentRegistrationFields(school),
    onEventInviteAction: getValue(school, ['triggers', 'onEventInviteAction'], 'AUTO_ACCEPT'),
    onTournamentInviteAction: getValue(school, ['triggers', 'onTournamentInviteAction'], 'MANUAL'),
    onTournamentEventInviteAction: getValue(school, ['triggers', 'onTournamentEventInviteAction'], 'AUTO_ACCEPT'),
    onInviteEmailNotification: getBoolValue(school, ['triggers', 'onInviteEmailNotification']),
    photoAccessPreset: getValue(school, ['photoAccessPreset'], ''),
    clubInvitations: getValue(school, ['clubInvitations'], ''),
    shortCode: getValue(school, ['shortCode'], ''),
    county: getValue(school, ['county'], ''),
    twitterAccountName: getValue(school, ['twitterAccountName'], ''),
    applicationFeePercentage: getValue(school, ['applicationFeePercentage'], ''),
    settingsCommentsEnabled: school.hasOwnProperty('commentsEnabled')
      ? getBoolValue(school, ['commentsEnabled'], true)
      : getBoolValue(school, ['settings', 'commentsEnabled'], true),
    settingsPhotosEnabled: school.hasOwnProperty('photosEnabled')
      ? getBoolValue(school, ['photosEnabled'], true)
      : getBoolValue(school, ['settings', 'photosEnabled'], true),
    settingsAvailabilityStudentEnabled: getBoolValue(school, ['settings', 'availabilityStudentEnabled'], true),
    settingsAvailabilityParentEnabled: getBoolValue(school, ['settings', 'availabilityParentEnabled'], true),
    studentImportForAdminAllowed: getBoolValue(school, ['studentImportForAdminAllowed']),
    availableForRegistration: getBoolValue(school, ['availableForRegistration']),
    studentSelfRegistrationEnabled: getBoolValue(school, ['studentSelfRegistrationEnabled']),
    notificationsEnabled: getBoolValue(school, ['notificationsEnabled']),
    allowedForFollowing: getBoolValue(school, ['allowedForFollowing']),
    eventPerformanceEnabledForParent: getBoolValue(school, ['eventPerformanceEnabledForParent']),
    eventCommunicationsAvailable: getBoolValue(school, ['eventCommunicationsAvailable'], true),
    tournamentCommunicationsAvailable: getBoolValue(school, ['tournamentCommunicationsAvailable'], true),
    isClubsEnabled: getBoolValue(school, ['isClubsEnabled']),
    isClubPriorityEnabled: getBoolValue(school, ['isClubPriorityEnabled']),
    isAbsenceRequestEnabled: getBoolValue(school, ['isAbsenceRequestEnabled']),
    isPaymentsEnabled: getBoolValue(school, ['isPaymentsEnabled']),
    canPublishWebSite: getBoolValue(school, ['canPublishWebSite']),
    canAcceptStaffRoles: getBoolValue(school, ['canAcceptStaffRoles']),
    isFavoriteSportsEnabled: getBoolValue(school, ['isFavoriteSportsEnabled']),
    canCreateStudentFromParentPermissionRequest: getBoolValue(school, ['canCreateStudentFromParentPermissionRequest']),
    isShowSchoolSelectOnPublicSite: getBoolValue(school, ['isShowSchoolSelectOnPublicSite']),
    isShowLeagueTableOnPublicSite: getBoolValue(school, ['isShowLeagueTableOnPublicSite']),
    isShowSchoolTableOnPublicSite: getBoolValue(school, ['isShowSchoolTableOnPublicSite']),
    isShowTournamentsOnPublicSite: getBoolValue(school, ['isShowTournamentsOnPublicSite']),
    isCreateSmsNotificationChannelForNewUsers: getBoolValue(school, ['isCreateSmsNotificationChannelForNewUsers']),
    isSendSmsWithAllSystemEvents: getBoolValue(school, ['isSendSmsWithAllSystemEvents']),
    isGeneralMessagesEnabled: getBoolValue(school, ['isGeneralMessagesEnabled']),
    isFirstAdminAutoAccept: getBoolValue(school, ['isFirstAdminAutoAccept']),
    isParentsAndStudentsAutoAccept: getBoolValue(school, ['isParentsAndStudentsAutoAccept']),
    allowedParentRelationships: Array.isArray(school.allowedParentRelationships)
      ? school.allowedParentRelationships
      : [],
    parentAutoLinkMatchMode: getValue(school, ['parentAutoLinkMatchMode'], 'EMAIL_AND_PHONE'),
    isCoachSeenOnlyAffectedEvents: getBoolValue(school, ['isCoachSeenOnlyAffectedEvents']),
    isTeacherSeenOnlyAffectedEvents: getBoolValue(school, ['isTeacherSeenOnlyAffectedEvents']),
    isOneOffCodeOnRegisterEnabled: getBoolValue(school, ['isOneOffCodeOnRegisterEnabled']),
    isEventUpdateNotificationCustomMessageEnabled: getBoolValue(school, [
      'isEventUpdateNotificationCustomMessageEnabled'
    ]),
    isFollowersAutoenroll: getBoolValue(school, ['isFollowersAutoenroll']),
    canSellPhotos: getBoolValue(school, ['canSellPhotos']),
    isShowStudentEventClashes: getBoolValue(school, ['isShowStudentEventClashes']),
    isShowAggregatedHouseLeagueOnPublicWebsite: getBoolValue(school, ['isShowAggregatedHouseLeagueOnPublicWebsite']),
    gmEmailHeaderColor: getValue(school, ['gmEmailHeaderColor'], '#1e3a5f'),
    gmEmailHeaderEnabled: getBoolValue(school, ['gmEmailHeaderEnabled'], true),
    gmEmailHeaderLogoEnabled: getBoolValue(school, ['gmEmailHeaderLogoEnabled'], true),
    gmEmailHeaderNameEnabled: getBoolValue(school, ['gmEmailHeaderNameEnabled'], true),
    gmEmailHeaderLogoAlignment: getValue(school, ['gmEmailHeaderLogoAlignment'], 'left') as 'left' | 'center' | 'right',
    gmEmailHeaderNamePlacement: getValue(school, ['gmEmailHeaderNamePlacement'], 'below') as
      | 'above'
      | 'left'
      | 'right'
      | 'below',
    gmEmailHeaderNameAlignment: getValue(school, ['gmEmailHeaderNameAlignment'], 'left') as 'left' | 'center' | 'right',
    gmEmailFooterHtml: getValue(school, ['gmEmailFooterHtml'], ''),
    gmEmailFooterBackgroundColor: getValue(school, ['gmEmailFooterBackgroundColor'], '#f8f9fa'),
    gmEmailFooterTextColor: getValue(school, ['gmEmailFooterTextColor'], '#64748b'),
    gmEmailFooterAlignment: getValue(school, ['gmEmailFooterAlignment'], 'left') as 'left' | 'center' | 'right'
  };
}

function setIfNotEmpty(target: any, field: string, value: string): void {
  const trimmedValue = value.trim();

  if (trimmedValue) {
    target[field] = trimmedValue;
  }
}

function buildSchoolUpdateData(form: SchoolForm): SuperAdminSchoolUpdateData {
  const data: SuperAdminSchoolUpdateData = {
    name: form.name.trim(),
    pic: form.pic,
    description: form.description,
    status: form.status,
    domain: form.domain.trim() || null,
    notificationEmail: form.notificationEmail,
    phone: form.phone,
    address: form.address,
    subscriptionPlan: form.subscriptionPlan || undefined,
    ageGroupsNaming: form.ageGroupsNaming || undefined,
    publicStudentViewType: form.publicStudentViewType || undefined,
    eduPhase: form.eduPhase || undefined,
    gender: form.gender || undefined,
    availableGender: form.availableGender || undefined,
    availableAges: form.availableAges,
    allowedPermissionPresets: form.allowedPermissionPresets,
    additionalPermissionRequestFields: form.additionalPermissionRequestFields,
    triggers: {
      onEventInviteAction: form.onEventInviteAction || 'AUTO_ACCEPT',
      onTournamentInviteAction: form.onTournamentInviteAction || 'MANUAL',
      onTournamentEventInviteAction: form.onTournamentEventInviteAction || 'AUTO_ACCEPT',
      onInviteEmailNotification: form.onInviteEmailNotification
    },
    photoAccessPreset: form.photoAccessPreset || undefined,
    clubInvitations: form.clubInvitations || undefined,
    shortCode: form.shortCode,
    county: form.county,
    twitterAccountName: form.twitterAccountName,
    settings: {
      commentsEnabled: form.settingsCommentsEnabled,
      photosEnabled: form.settingsPhotosEnabled,
      availabilityStudentEnabled: form.settingsAvailabilityStudentEnabled,
      availabilityParentEnabled: form.settingsAvailabilityParentEnabled
    },
    studentImportForAdminAllowed: form.studentImportForAdminAllowed,
    availableForRegistration: form.availableForRegistration,
    studentSelfRegistrationEnabled: form.studentSelfRegistrationEnabled,
    notificationsEnabled: form.notificationsEnabled,
    allowedForFollowing: form.allowedForFollowing,
    eventPerformanceEnabledForParent: form.eventPerformanceEnabledForParent,
    eventCommunicationsAvailable: form.eventCommunicationsAvailable,
    tournamentCommunicationsAvailable: form.tournamentCommunicationsAvailable,
    isClubsEnabled: form.isClubsEnabled,
    isClubPriorityEnabled: form.isClubPriorityEnabled,
    isAbsenceRequestEnabled: form.isAbsenceRequestEnabled,
    isPaymentsEnabled: form.isPaymentsEnabled,
    canPublishWebSite: form.canPublishWebSite,
    canAcceptStaffRoles: form.canAcceptStaffRoles,
    isFavoriteSportsEnabled: form.isFavoriteSportsEnabled,
    canCreateStudentFromParentPermissionRequest: form.canCreateStudentFromParentPermissionRequest,
    isShowSchoolSelectOnPublicSite: form.isShowSchoolSelectOnPublicSite,
    isShowLeagueTableOnPublicSite: form.isShowLeagueTableOnPublicSite,
    isShowSchoolTableOnPublicSite: form.isShowSchoolTableOnPublicSite,
    isShowTournamentsOnPublicSite: form.isShowTournamentsOnPublicSite,
    isCreateSmsNotificationChannelForNewUsers: form.isCreateSmsNotificationChannelForNewUsers,
    isSendSmsWithAllSystemEvents: form.isSendSmsWithAllSystemEvents,
    isGeneralMessagesEnabled: form.isGeneralMessagesEnabled,
    isFirstAdminAutoAccept: form.isFirstAdminAutoAccept,
    isParentsAndStudentsAutoAccept: form.isParentsAndStudentsAutoAccept,
    allowedParentRelationships: form.allowedParentRelationships,
    parentAutoLinkMatchMode: form.parentAutoLinkMatchMode || undefined,
    isCoachSeenOnlyAffectedEvents: form.isCoachSeenOnlyAffectedEvents,
    isTeacherSeenOnlyAffectedEvents: form.isTeacherSeenOnlyAffectedEvents,
    isOneOffCodeOnRegisterEnabled: form.isOneOffCodeOnRegisterEnabled,
    isEventUpdateNotificationCustomMessageEnabled: form.isEventUpdateNotificationCustomMessageEnabled,
    isFollowersAutoenroll: form.isFollowersAutoenroll,
    canSellPhotos: form.canSellPhotos,
    isShowStudentEventClashes: form.isShowStudentEventClashes,
    isShowAggregatedHouseLeagueOnPublicWebsite: form.isShowAggregatedHouseLeagueOnPublicWebsite,
    gmEmailHeaderColor: form.gmEmailHeaderColor,
    gmEmailHeaderEnabled: form.gmEmailHeaderEnabled,
    gmEmailHeaderLogoEnabled: form.gmEmailHeaderLogoEnabled,
    gmEmailHeaderNameEnabled: form.gmEmailHeaderNameEnabled,
    gmEmailHeaderLogoAlignment: form.gmEmailHeaderLogoAlignment,
    gmEmailHeaderNamePlacement: form.gmEmailHeaderNamePlacement,
    gmEmailHeaderNameAlignment: form.gmEmailHeaderNameAlignment,
    gmEmailFooterHtml: form.gmEmailFooterHtml,
    gmEmailFooterBackgroundColor: form.gmEmailFooterBackgroundColor,
    gmEmailFooterTextColor: form.gmEmailFooterTextColor,
    gmEmailFooterAlignment: form.gmEmailFooterAlignment
  };

  setIfNotEmpty(data, 'email', form.email);
  setIfNotEmpty(data, 'sportsDepartmentEmail', form.sportsDepartmentEmail);

  if (form.applicationFeePercentage.trim()) {
    data.applicationFeePercentage = Number(form.applicationFeePercentage);
  }

  return data;
}

function renderOption(option: { value: string; text: string }): React.ReactNode {
  return (
    <option key={option.value || 'empty'} value={option.value}>
      {option.text}
    </option>
  );
}

export class SuperAdminSchool extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      school: undefined,
      isLoading: false,
      isSaving: false,
      isLogoUploading: false,
      errorMessage: '',
      successMessage: '',
      currentTabIndex: 0,
      isEditMode: false,
      form: schoolToForm({})
    };
  }

  componentDidMount() {
    this.loadSchool();
  }

  loadSchool = (): void => {
    const { schoolId } = this.props.match.params;

    this.setState({ isLoading: true, errorMessage: '', successMessage: '' });

    getSuperAdminSchool(schoolId)
      .then(school => {
        this.setState({
          school,
          form: schoolToForm(school),
          isLoading: false,
          errorMessage: ''
        });
      })
      .catch(error => {
        console.error('Can not load superadmin school', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load school. Please check the session and try again.'
        });
      });
  };

  onTabClick = (event, index: number): void => {
    this.setState(state => ({ currentTabIndex: index, isEditMode: false, form: schoolToForm(state.school) }));
  };

  onEditClick = (): void => {
    this.setState({
      isEditMode: true,
      form: schoolToForm(this.state.school),
      errorMessage: '',
      successMessage: ''
    });
  };

  onCancelEditClick = (): void => {
    this.setState({
      isEditMode: false,
      form: schoolToForm(this.state.school),
      errorMessage: ''
    });
  };

  onRelationshipToggle = (rel: string): void => {
    const current: string[] = this.state.form.allowedParentRelationships || [];
    const next = current.includes(rel) ? current.filter(r => r !== rel) : [...current, rel];
    this.setState({ form: { ...this.state.form, allowedParentRelationships: next } } as any);
  };

  onAvailableAgeToggle = (age: number): void => {
    const current: number[] = this.state.form.availableAges || [];
    const next = current.includes(age) ? current.filter(a => a !== age) : [...current, age].sort((a, b) => a - b);
    this.setState({ form: { ...this.state.form, availableAges: next } } as any);
  };

  onPermissionPresetToggle = (preset: string): void => {
    const current = this.state.form.allowedPermissionPresets || {};
    this.setState({
      form: {
        ...this.state.form,
        allowedPermissionPresets: {
          ...current,
          [preset]: current[preset] === false
        }
      }
    } as any);
  };

  onFormChange = (event): void => {
    const { name, value, type, checked } = event.target;

    this.setState({
      form: {
        ...this.state.form,
        [name]: type === 'checkbox' ? checked : value
      }
    } as any);
  };

  onParentRegistrationFieldChange = (field: string, event: React.ChangeEvent<HTMLSelectElement>): void => {
    this.setState(
      previousState =>
        ({
          form: {
            ...previousState.form,
            additionalPermissionRequestFields: {
              ...previousState.form.additionalPermissionRequestFields,
              [field]: event.target.value
            }
          }
        } as State)
    );
  };

  onLogoChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';

    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.setState({ errorMessage: 'Please select an image file.' });
      return;
    }

    this.setState({ isLogoUploading: true, errorMessage: '', successMessage: '' });
    uploadSuperAdminImage(file)
      .then(response => {
        const pic = getUploadedImageUrl(response);
        if (!pic) throw new Error('Image upload returned no URL.');

        this.setState(
          previousState =>
            ({
              isLogoUploading: false,
              form: {
                ...previousState.form,
                pic
              }
            } as State)
        );
      })
      .catch(error => {
        console.error('Can not upload school logo', error);
        this.setState({
          isLogoUploading: false,
          errorMessage: 'Can not upload the school logo. Please try another image.'
        });
      });
  };

  onSaveSchoolClick = (): void => {
    const { schoolId } = this.props.match.params;
    const { form } = this.state;

    if (!form.name.trim()) {
      this.setState({ errorMessage: 'School name is required.' });
      return;
    }

    if (form.applicationFeePercentage.trim() && isNaN(Number(form.applicationFeePercentage))) {
      this.setState({ errorMessage: 'Application fee percentage should be a number.' });
      return;
    }

    const data = buildSchoolUpdateData(form);

    this.setState({ isSaving: true, errorMessage: '', successMessage: '' });

    updateSuperAdminSchool(schoolId, data)
      .then(school => {
        const mergedSchool = {
          ...school,
          settings: {
            ...(school.settings || {}),
            commentsEnabled: form.settingsCommentsEnabled,
            photosEnabled: form.settingsPhotosEnabled,
            availabilityStudentEnabled: form.settingsAvailabilityStudentEnabled,
            availabilityParentEnabled: form.settingsAvailabilityParentEnabled
          }
        };
        this.setState({
          school: mergedSchool,
          form: schoolToForm(mergedSchool),
          isSaving: false,
          isEditMode: false,
          successMessage: 'School has been updated.'
        });
      })
      .catch(error => {
        console.error('Can not update superadmin school', error);
        this.setState({
          isSaving: false,
          errorMessage: 'Can not update school. Please check fields and try again.'
        });
      });
  };

  renderReadonlyRow(label: string, value: React.ReactNode): React.ReactNode {
    return (
      <tr>
        <td>{label}</td>
        <td>{value}</td>
      </tr>
    );
  }

  renderTextInput(label: string, name: string, colClass: string = 'col-md-6', type: string = 'text'): React.ReactNode {
    const { form, isSaving } = this.state;

    return (
      <div className={`form-group ${colClass}`}>
        <label>{label}</label>
        <input
          className="form-control"
          name={name}
          type={type}
          value={form[name] || ''}
          onChange={this.onFormChange}
          disabled={isSaving}
        />
      </div>
    );
  }

  renderSelect(
    label: string,
    name: string,
    options: Array<{ value: string; text: string }>,
    colClass: string = 'col-md-4'
  ): React.ReactNode {
    const { form, isSaving } = this.state;

    return (
      <div className={`form-group ${colClass}`}>
        <label>{label}</label>
        <select
          className="form-control"
          name={name}
          value={form[name] || ''}
          onChange={this.onFormChange}
          disabled={isSaving}
        >
          {options.map(renderOption)}
        </select>
      </div>
    );
  }

  renderCheckbox(label: string, name: string): React.ReactNode {
    const { form, isSaving } = this.state;

    return (
      <div className="col-md-4 mb-2">
        <div className="form-check">
          <input
            className="form-check-input"
            id={`school_${name}`}
            name={name}
            type="checkbox"
            checked={Boolean(form[name])}
            onChange={this.onFormChange}
            disabled={isSaving}
          />
          <label className="form-check-label" htmlFor={`school_${name}`}>
            {label}
          </label>
        </div>
      </div>
    );
  }

  renderAvailableAgesEditor(): React.ReactNode {
    const selected = this.state.form.availableAges || [];

    return (
      <div className="form-group">
        <label>
          <strong>Available ages</strong>
          <span className="text-muted ml-2 small">({selected.length} selected)</span>
        </label>
        <div
          style={{
            border: '1px solid #ced4da',
            borderRadius: '4px',
            padding: '12px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: '6px'
          }}
        >
          {AVAILABLE_AGE_OPTIONS.map(option => (
            <div key={option.value} className="form-check">
              <input
                className="form-check-input"
                id={`age_${option.value}`}
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={() => this.onAvailableAgeToggle(option.value)}
                disabled={this.state.isSaving}
              />
              <label className="form-check-label" htmlFor={`age_${option.value}`}>
                {option.text}
              </label>
            </div>
          ))}
        </div>
        <div className="mt-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary mr-2"
            disabled={this.state.isSaving}
            onClick={() =>
              this.setState({
                form: { ...this.state.form, availableAges: AVAILABLE_AGE_OPTIONS.map(o => o.value) }
              } as any)
            }
          >
            Select all
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={this.state.isSaving}
            onClick={() => this.setState({ form: { ...this.state.form, availableAges: [] } } as any)}
          >
            Clear all
          </button>
        </div>
      </div>
    );
  }

  renderPermissionPresetEditor(): React.ReactNode {
    const presets = this.state.form.allowedPermissionPresets || {};

    return (
      <div className="form-group">
        <label>
          <strong>Allowed registration roles</strong>
        </label>
        <div className="d-flex flex-wrap" style={{ gap: '4px 18px' }}>
          {PERMISSION_PRESET_OPTIONS.map(option => (
            <label key={option.value} className="mb-1" style={{ minWidth: 170 }}>
              <input
                type="checkbox"
                className="mr-2"
                checked={presets[option.value] !== false}
                onChange={() => this.onPermissionPresetToggle(option.value)}
                disabled={this.state.isSaving}
              />
              {option.text}
            </label>
          ))}
        </div>
      </div>
    );
  }

  renderLogoEditor(): React.ReactNode {
    const { form, isLogoUploading, isSaving } = this.state;
    const isDisabled = isLogoUploading || isSaving;

    return (
      <div className="form-group">
        <label>
          <strong>School logo</strong>
        </label>
        <div className="d-flex align-items-center flex-wrap">
          <div
            className="border rounded bg-light d-flex align-items-center justify-content-center mr-3 mb-2"
            style={{ width: 112, height: 112, overflow: 'hidden' }}
          >
            {form.pic ? (
              <img
                src={form.pic}
                alt="School logo preview"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <span className="text-muted small">No logo</span>
            )}
          </div>
          <div className="mb-2">
            <label className={`btn btn-sm btn-outline-primary mb-2 ${isDisabled ? 'disabled' : ''}`}>
              {isLogoUploading ? 'Uploading…' : form.pic ? 'Replace logo' : 'Upload logo'}
              <input
                type="file"
                accept="image/*"
                className="d-none"
                disabled={isDisabled}
                onChange={this.onLogoChange}
              />
            </label>
            {form.pic ? (
              <button
                type="button"
                className="btn btn-sm btn-link text-danger d-block p-0"
                disabled={isDisabled}
                onClick={() => this.setState({ form: { ...form, pic: '' } })}
              >
                Remove logo
              </button>
            ) : null}
            <div className="text-muted small mt-1">PNG, JPEG, GIF or another browser-supported image.</div>
          </div>
        </div>
      </div>
    );
  }

  renderParentRegistrationFieldsEditor(): React.ReactNode {
    const values = this.state.form.additionalPermissionRequestFields;

    return (
      <div className="form-group">
        <label>
          <strong>Child details requested during parent registration</strong>
        </label>
        <div className="text-muted small mb-2">
          Choose whether each field is required, optional or completely hidden from the parent registration form.
        </div>
        <div className="row">
          {PARENT_REGISTRATION_FIELDS.map(field => (
            <div className="col-md-6 col-lg-4" key={field.key}>
              <div className="form-group">
                <label htmlFor={`parent_registration_${field.key}`}>{field.label}</label>
                <select
                  id={`parent_registration_${field.key}`}
                  className="form-control"
                  value={values[field.key] || 'OPTIONAL'}
                  disabled={this.state.isSaving}
                  onChange={event => this.onParentRegistrationFieldChange(field.key, event)}
                >
                  {PARENT_REGISTRATION_FIELD_STATUS_OPTIONS.map(renderOption)}
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderParentRegistrationFieldsReadonly(): React.ReactNode {
    const values = getParentRegistrationFields(this.state.school);

    return PARENT_REGISTRATION_FIELDS.map(field => this.renderReadonlyRow(field.label, formatValue(values[field.key])));
  }
  renderOverviewReadonly(): React.ReactNode {
    const { school } = this.state;

    return (
      <div>
        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-primary" type="button" onClick={this.onEditClick}>
            Edit school
          </button>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="card mb-3">
              <div className="card-header">Main details</div>
              <div className="card-body p-0">
                <div className="p-3 border-bottom d-flex align-items-center">
                  <div
                    className="border rounded bg-light d-flex align-items-center justify-content-center mr-3"
                    style={{ width: 88, height: 88, overflow: 'hidden' }}
                  >
                    {school.pic ? (
                      <img
                        src={school.pic}
                        alt={`${school.name || 'School'} logo`}
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <span className="text-muted small">No logo</span>
                    )}
                  </div>
                  <div>
                    <div className="font-weight-bold">School logo</div>
                    <div className="text-muted small">Used in school-facing and family interfaces.</div>
                  </div>
                </div>
                <table className="table table-sm mb-0">
                  <tbody>
                    {this.renderReadonlyRow('Name', getValue(school, ['name']))}
                    {this.renderReadonlyRow('Kind', getValue(school, ['kind'], 'School'))}
                    {this.renderReadonlyRow('Status', formatValue(getValue(school, ['status'], '')))}
                    {this.renderReadonlyRow(
                      'Subscription plan',
                      formatValue(getValue(school, ['subscriptionPlan'], ''))
                    )}
                    {this.renderReadonlyRow('Domain', getValue(school, ['domain']))}
                    {this.renderReadonlyRow('Public site', getPublicSiteUrl(school))}
                    {this.renderReadonlyRow('Short code', getValue(school, ['shortCode']))}
                    {this.renderReadonlyRow('Description', getValue(school, ['description']))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card mb-3">
              <div className="card-header">Contact and location</div>
              <div className="card-body p-0">
                <table className="table table-sm mb-0">
                  <tbody>
                    {this.renderReadonlyRow('Email', getValue(school, ['email']))}
                    {this.renderReadonlyRow('Notification email', getValue(school, ['notificationEmail']))}
                    {this.renderReadonlyRow('Sports department email', getValue(school, ['sportsDepartmentEmail']))}
                    {this.renderReadonlyRow('Phone', getValue(school, ['phone']))}
                    {this.renderReadonlyRow('Postcode', getPostcode(school))}
                    {this.renderReadonlyRow('Address', getValue(school, ['address']))}
                    {this.renderReadonlyRow('County', getValue(school, ['county']))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="card mb-3">
              <div className="card-header">School settings</div>
              <div className="card-body p-0">
                <table className="table table-sm mb-0">
                  <tbody>
                    {this.renderReadonlyRow('Age groups naming', getValue(school, ['ageGroupsNaming']))}
                    {this.renderReadonlyRow(
                      'Public student view',
                      formatValue(getValue(school, ['publicStudentViewType'], ''))
                    )}
                    {this.renderReadonlyRow('Edu phase', formatValue(getValue(school, ['eduPhase'], '')))}
                    {this.renderReadonlyRow('Gender', formatValue(getValue(school, ['gender'], '')))}
                    {this.renderReadonlyRow('Available gender', formatValue(getValue(school, ['availableGender'], '')))}
                    {this.renderReadonlyRow(
                      'Photo access preset',
                      formatValue(getValue(school, ['photoAccessPreset'], ''))
                    )}
                    {this.renderReadonlyRow('Club invitations', formatValue(getValue(school, ['clubInvitations'], '')))}
                    {this.renderReadonlyRow('Application fee %', getValue(school, ['applicationFeePercentage']))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card mb-3">
              <div className="card-header">Feature flags</div>
              <div className="card-body p-0">
                <table className="table table-sm mb-0">
                  <tbody>
                    {this.renderReadonlyRow('Registration available', formatBoolean(school.availableForRegistration))}
                    {this.renderReadonlyRow(
                      'Student self registration',
                      formatBoolean(school.studentSelfRegistrationEnabled)
                    )}
                    {this.renderReadonlyRow('Notifications enabled', formatBoolean(school.notificationsEnabled))}
                    {this.renderReadonlyRow(
                      'Chats available',
                      formatBoolean(school.eventCommunicationsAvailable !== false)
                    )}
                    {this.renderReadonlyRow(
                      'Tournament communications available',
                      formatBoolean(school.tournamentCommunicationsAvailable !== false)
                    )}
                    {this.renderReadonlyRow('Clubs enabled', formatBoolean(school.isClubsEnabled))}
                    {this.renderReadonlyRow('Club priority enabled', formatBoolean(school.isClubPriorityEnabled))}
                    {this.renderReadonlyRow('Payments enabled', formatBoolean(school.isPaymentsEnabled))}
                    {this.renderReadonlyRow('General messages enabled', formatBoolean(school.isGeneralMessagesEnabled))}
                    {this.renderReadonlyRow('Can accept staff roles', formatBoolean(school.canAcceptStaffRoles))}
                    {this.renderReadonlyRow('Can sell photos', formatBoolean(school.canSellPhotos))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            <div className="card mb-3">
              <div className="card-header">Parent auto-link settings</div>
              <div className="card-body p-0">
                <table className="table table-sm mb-0">
                  <tbody>
                    {this.renderReadonlyRow(
                      'Auto-accept enabled',
                      formatBoolean(school.isParentsAndStudentsAutoAccept)
                    )}
                    {this.renderReadonlyRow(
                      'Match mode',
                      formatValue(getValue(school, ['parentAutoLinkMatchMode'], ''))
                    )}
                    {this.renderReadonlyRow(
                      'Allowed relationships',
                      (() => {
                        const rels: string[] = Array.isArray(school.allowedParentRelationships)
                          ? school.allowedParentRelationships
                          : [];
                        return rels.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {rels.map(r => (
                              <span key={r} className="badge badge-secondary">
                                {r}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted">None configured</span>
                        );
                      })()
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderOverviewEdit(): React.ReactNode {
    const { isSaving } = this.state;

    return (
      <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>Edit school</strong>
          <div>
            <button
              className="btn btn-sm btn-outline-secondary mr-2"
              type="button"
              onClick={this.onCancelEditClick}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              className="btn btn-sm btn-primary"
              type="button"
              onClick={this.onSaveSchoolClick}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
        <div className="card-body">
          {this.renderLogoEditor()}

          <div className="form-row">
            {this.renderTextInput('Name', 'name')}
            {this.renderSelect('Status', 'status', SCHOOL_STATUS_OPTIONS, 'col-md-3')}
            {this.renderSelect('Subscription plan', 'subscriptionPlan', SUBSCRIPTION_PLAN_OPTIONS, 'col-md-3')}
          </div>

          <div className="form-row">
            {this.renderTextInput('Domain', 'domain', 'col-md-4')}
            {this.renderTextInput('Short code', 'shortCode', 'col-md-4')}
            {this.renderTextInput('County', 'county', 'col-md-4')}
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              className="form-control"
              name="description"
              rows={3}
              value={this.state.form.description}
              onChange={this.onFormChange}
              disabled={isSaving}
            />
          </div>

          <h5 className="mt-4">Contact and location</h5>
          <div className="form-row">
            {this.renderTextInput('Email', 'email', 'col-md-4', 'email')}
            {this.renderTextInput('Notification email', 'notificationEmail', 'col-md-4')}
            {this.renderTextInput('Sports department email', 'sportsDepartmentEmail', 'col-md-4', 'email')}
          </div>
          <div className="form-row">
            {this.renderTextInput('Phone', 'phone', 'col-md-4')}
            {this.renderTextInput('Address', 'address', 'col-md-8')}
          </div>

          <h5 className="mt-4">School settings</h5>
          <div className="form-row">
            {this.renderSelect('Age groups naming', 'ageGroupsNaming', AGE_GROUPS_NAMING_OPTIONS)}
            {this.renderSelect('Public student view', 'publicStudentViewType', STUDENT_VIEW_OPTIONS)}
            {this.renderSelect('Edu phase', 'eduPhase', EDU_PHASE_OPTIONS)}
            {this.renderSelect('Gender', 'gender', GENDER_OPTIONS)}
            {this.renderSelect('Available gender', 'availableGender', SCHOOL_GENDER_OPTIONS)}
            {this.renderSelect('Photo access preset', 'photoAccessPreset', PHOTO_ACCESS_PRESET_OPTIONS)}
            {this.renderSelect('Club invitations', 'clubInvitations', CLUB_INVITATIONS_OPTIONS)}
            {this.renderTextInput('Application fee %', 'applicationFeePercentage', 'col-md-4', 'number')}
            {this.renderTextInput('Twitter account name', 'twitterAccountName', 'col-md-4')}
          </div>
          {this.renderAvailableAgesEditor()}

          <h5 className="mt-4">Registration</h5>
          <div className="row">
            {this.renderCheckbox('Available for registration', 'availableForRegistration')}
            {this.renderCheckbox('Student self registration enabled', 'studentSelfRegistrationEnabled')}
            {this.renderCheckbox('Can accept staff roles', 'canAcceptStaffRoles')}
            {this.renderCheckbox('First admin auto accept', 'isFirstAdminAutoAccept')}
            {this.renderCheckbox('Parents and students auto accept', 'isParentsAndStudentsAutoAccept')}
            {this.renderCheckbox('One-off code on register enabled', 'isOneOffCodeOnRegisterEnabled')}
          </div>
          {this.renderPermissionPresetEditor()}
          {this.renderParentRegistrationFieldsEditor()}

          <h5 className="mt-4">Invite settings</h5>
          <div className="form-row">
            {this.renderSelect('Event invites', 'onEventInviteAction', INVITE_ACTION_OPTIONS, 'col-md-4')}
            {this.renderSelect('Tournament invites', 'onTournamentInviteAction', INVITE_ACTION_OPTIONS, 'col-md-4')}
            {this.renderSelect(
              'Tournament event invites',
              'onTournamentEventInviteAction',
              INVITE_ACTION_OPTIONS,
              'col-md-4'
            )}
          </div>
          <div className="row">
            {this.renderCheckbox('Send email notification on invite', 'onInviteEmailNotification')}
          </div>

          <h5 className="mt-4">Feature flags</h5>
          <div className="row">
            {this.renderCheckbox('Comments enabled', 'settingsCommentsEnabled')}
            {this.renderCheckbox('Photos enabled', 'settingsPhotosEnabled')}
            {this.renderCheckbox('Student availability requests enabled', 'settingsAvailabilityStudentEnabled')}
            {this.renderCheckbox('Parent availability requests enabled', 'settingsAvailabilityParentEnabled')}
            {this.renderCheckbox('Student import for admin allowed', 'studentImportForAdminAllowed')}
            {this.renderCheckbox('Notifications enabled', 'notificationsEnabled')}
            {this.renderCheckbox('Allowed for following', 'allowedForFollowing')}
            {this.renderCheckbox('Event performance enabled for parent', 'eventPerformanceEnabledForParent')}
            {this.renderCheckbox('Chats available', 'eventCommunicationsAvailable')}
            {this.renderCheckbox('Tournament communications available', 'tournamentCommunicationsAvailable')}
            {this.renderCheckbox('Clubs enabled', 'isClubsEnabled')}
            {this.renderCheckbox('Club priority enabled', 'isClubPriorityEnabled')}
            {this.renderCheckbox('Absence request enabled', 'isAbsenceRequestEnabled')}
            {this.renderCheckbox('Payments enabled', 'isPaymentsEnabled')}
            {this.renderCheckbox('Can publish website', 'canPublishWebSite')}
            {this.renderCheckbox('Favorite sports enabled', 'isFavoriteSportsEnabled')}
            {this.renderCheckbox(
              'Can create student from parent permission request',
              'canCreateStudentFromParentPermissionRequest'
            )}
            {this.renderCheckbox('Show school select on public site', 'isShowSchoolSelectOnPublicSite')}
            {this.renderCheckbox('Show league table on public site', 'isShowLeagueTableOnPublicSite')}
            {this.renderCheckbox('Show school table on public site', 'isShowSchoolTableOnPublicSite')}
            {this.renderCheckbox('Show tournaments on public site', 'isShowTournamentsOnPublicSite')}
            {this.renderCheckbox('Create SMS channel for new users', 'isCreateSmsNotificationChannelForNewUsers')}
            {this.renderCheckbox('Send SMS with all system events', 'isSendSmsWithAllSystemEvents')}
            {this.renderCheckbox('General messages enabled', 'isGeneralMessagesEnabled')}
            {this.renderCheckbox('Coach sees only affected events', 'isCoachSeenOnlyAffectedEvents')}
            {this.renderCheckbox('Teacher sees only affected events', 'isTeacherSeenOnlyAffectedEvents')}
            {this.renderCheckbox(
              'Event update custom message enabled',
              'isEventUpdateNotificationCustomMessageEnabled'
            )}
            {this.renderCheckbox('Followers auto-enroll', 'isFollowersAutoenroll')}
            {this.renderCheckbox('Can sell photos', 'canSellPhotos')}
            {this.renderCheckbox('Show student event clashes', 'isShowStudentEventClashes')}
            {this.renderCheckbox(
              'Show aggregated house league on public website',
              'isShowAggregatedHouseLeagueOnPublicWebsite'
            )}
          </div>

          <h5 className="mt-4">Parent auto-link settings</h5>
          <p className="text-muted small mb-3">
            When enabled, newly registered parents are automatically matched against student next-of-kin records using
            the selected contact fields. Only relationships in the allowed list will trigger auto-linking.
          </p>

          <div className="form-row align-items-end mb-3">
            <div className="col-md-4">
              {this.renderCheckbox('Auto-accept parents (auto-link enabled)', 'isParentsAndStudentsAutoAccept')}
            </div>
            {this.renderSelect(
              'Match mode',
              'parentAutoLinkMatchMode',
              PARENT_AUTO_LINK_MATCH_MODE_OPTIONS,
              'col-md-4'
            )}
          </div>

          <div className="form-group">
            <label>
              <strong>Allowed relationships</strong>
              <span className="text-muted ml-2 small">
                ({(this.state.form.allowedParentRelationships || []).length} selected)
              </span>
            </label>
            <div
              style={{
                border: '1px solid #ced4da',
                borderRadius: '4px',
                padding: '12px',
                maxHeight: '320px',
                overflowY: 'auto',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: '6px'
              }}
            >
              {ALL_PARENT_RELATIONSHIPS.map(rel => {
                const checked = (this.state.form.allowedParentRelationships || []).includes(rel);
                return (
                  <div key={rel} className="form-check">
                    <input
                      className="form-check-input"
                      id={`rel_${rel}`}
                      type="checkbox"
                      checked={checked}
                      onChange={() => this.onRelationshipToggle(rel)}
                      disabled={this.state.isSaving}
                    />
                    <label className="form-check-label" htmlFor={`rel_${rel}`}>
                      {rel}
                    </label>
                  </div>
                );
              })}
            </div>
            <div className="mt-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary mr-2"
                disabled={this.state.isSaving}
                onClick={() =>
                  this.setState({
                    form: { ...this.state.form, allowedParentRelationships: [...ALL_PARENT_RELATIONSHIPS] }
                  } as any)
                }
              >
                Select all
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={this.state.isSaving}
                onClick={() => this.setState({ form: { ...this.state.form, allowedParentRelationships: [] } } as any)}
              >
                Clear all
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderOverview(): React.ReactNode {
    return this.state.isEditMode ? this.renderMainDetailsEdit() : this.renderOverviewReadonly();
  }

  renderSectionEditActions(label: string): React.ReactNode {
    return (
      <div className="d-flex justify-content-end mb-3" style={{ gap: 8 }}>
        <button
          className="btn btn-outline-secondary"
          type="button"
          onClick={this.onCancelEditClick}
          disabled={this.state.isSaving}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          type="button"
          onClick={this.onSaveSchoolClick}
          disabled={this.state.isSaving}
        >
          {this.state.isSaving ? 'Saving…' : `Save ${label}`}
        </button>
      </div>
    );
  }

  renderMainDetailsEdit(): React.ReactNode {
    return (
      <div>
        {this.renderSectionEditActions('school details')}
        <div className="card mb-3">
          <div className="card-header">Main details</div>
          <div className="card-body">
            {this.renderLogoEditor()}
            <div className="form-row">
              {this.renderTextInput('Name', 'name')}
              {this.renderSelect('Status', 'status', SCHOOL_STATUS_OPTIONS, 'col-md-3')}
              {this.renderSelect('Subscription plan', 'subscriptionPlan', SUBSCRIPTION_PLAN_OPTIONS, 'col-md-3')}
            </div>
            <div className="form-row">
              {this.renderTextInput('Short code', 'shortCode', 'col-md-4')}
              {this.renderTextInput('County', 'county', 'col-md-4')}
              {this.renderTextInput('Twitter account name', 'twitterAccountName', 'col-md-4')}
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                className="form-control"
                name="description"
                rows={3}
                value={this.state.form.description}
                onChange={this.onFormChange}
                disabled={this.state.isSaving}
              />
            </div>
          </div>
        </div>
        <div className="card mb-3">
          <div className="card-header">Contact details</div>
          <div className="card-body">
            <div className="form-row">
              {this.renderTextInput('Email', 'email', 'col-md-4', 'email')}
              {this.renderTextInput('Notification email', 'notificationEmail', 'col-md-4', 'email')}
              {this.renderTextInput('Sports department email', 'sportsDepartmentEmail', 'col-md-4', 'email')}
            </div>
            <div className="form-row">
              {this.renderTextInput('Phone', 'phone', 'col-md-4')}
              {this.renderTextInput('Address', 'address', 'col-md-8')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderSettingsTab(): React.ReactNode {
    const { school } = this.state;
    if (this.state.isEditMode) return this.renderSettingsEdit();

    const ages = Array.isArray(school.availableAges) ? school.availableAges : [];

    return (
      <div>
        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-primary" type="button" onClick={this.onEditClick}>
            Edit school
          </button>
        </div>
        <div className="card mb-3">
          <div className="card-header">School settings</div>
          <div className="card-body p-0">
            <table className="table table-sm mb-0">
              <tbody>
                {this.renderReadonlyRow('Age groups naming', getValue(school, ['ageGroupsNaming']))}
                {this.renderReadonlyRow(
                  'Available ages',
                  ages.length > 0 ? ages.map(age => propz.get(AGE_GROUPS, ['ENGLISH', age], age)).join(', ') : '—'
                )}
                {this.renderReadonlyRow('Available gender', formatValue(getValue(school, ['availableGender'], '')))}
                {this.renderReadonlyRow('Edu phase', formatValue(getValue(school, ['eduPhase'], '')))}
                {this.renderReadonlyRow('School gender', formatValue(getValue(school, ['gender'], '')))}
                {this.renderReadonlyRow(
                  'Public student view',
                  formatValue(getValue(school, ['publicStudentViewType'], ''))
                )}
                {this.renderReadonlyRow(
                  'Photo access preset',
                  formatValue(getValue(school, ['photoAccessPreset'], ''))
                )}
                {this.renderReadonlyRow('Club invitations', formatValue(getValue(school, ['clubInvitations'], '')))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  renderRegistrationTab(): React.ReactNode {
    const { school } = this.state;
    if (this.state.isEditMode) return this.renderRegistrationEdit();

    const presets = school.allowedPermissionPresets || {};
    const activePresets = PERMISSION_PRESET_OPTIONS.filter(option => presets[option.value] !== false).map(
      option => option.text
    );

    return (
      <div>
        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-primary" type="button" onClick={this.onEditClick}>
            Edit school
          </button>
        </div>
        <div className="card mb-3">
          <div className="card-header">Registration</div>
          <div className="card-body p-0">
            <table className="table table-sm mb-0">
              <tbody>
                {this.renderReadonlyRow('Available for registration', formatBoolean(school.availableForRegistration))}
                {this.renderReadonlyRow(
                  'Student self registration',
                  formatBoolean(school.studentSelfRegistrationEnabled)
                )}
                {this.renderReadonlyRow('Can accept staff roles', formatBoolean(school.canAcceptStaffRoles))}
                {this.renderReadonlyRow(
                  'One-off code on register',
                  formatBoolean(school.isOneOffCodeOnRegisterEnabled)
                )}
                {this.renderReadonlyRow('First admin auto accept', formatBoolean(school.isFirstAdminAutoAccept))}
                {this.renderReadonlyRow(
                  'Parents and students auto accept',
                  formatBoolean(school.isParentsAndStudentsAutoAccept)
                )}
                {this.renderReadonlyRow(
                  'Allowed registration roles',
                  activePresets.length > 0 ? activePresets.join(', ') : 'None'
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card mb-3">
          <div className="card-header">Parent registration child fields</div>
          <div className="card-body p-0">
            <table className="table table-sm mb-0">
              <tbody>{this.renderParentRegistrationFieldsReadonly()}</tbody>
            </table>
          </div>
        </div>
        <div className="card mb-3">
          <div className="card-header">Parent matching</div>
          <div className="card-body p-0">
            <table className="table table-sm mb-0">
              <tbody>
                {this.renderReadonlyRow(
                  'Create student from parent request',
                  formatBoolean(school.canCreateStudentFromParentPermissionRequest)
                )}
                {this.renderReadonlyRow('Match mode', formatValue(getValue(school, ['parentAutoLinkMatchMode'], '')))}
                {this.renderReadonlyRow(
                  'Allowed relationships',
                  (school.allowedParentRelationships || []).join(', ') || 'None'
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  renderSettingsEdit(): React.ReactNode {
    return (
      <div>
        {this.renderSectionEditActions('school settings')}
        <div className="card mb-3">
          <div className="card-header">School settings</div>
          <div className="card-body">
            <div className="form-row">
              {this.renderSelect('Age groups naming', 'ageGroupsNaming', AGE_GROUPS_NAMING_OPTIONS)}
              {this.renderSelect('Public student view', 'publicStudentViewType', STUDENT_VIEW_OPTIONS)}
              {this.renderSelect('Edu phase', 'eduPhase', EDU_PHASE_OPTIONS)}
              {this.renderSelect('Gender', 'gender', GENDER_OPTIONS)}
              {this.renderSelect('Available gender', 'availableGender', SCHOOL_GENDER_OPTIONS)}
              {this.renderSelect('Photo access preset', 'photoAccessPreset', PHOTO_ACCESS_PRESET_OPTIONS)}
              {this.renderSelect('Club invitations', 'clubInvitations', CLUB_INVITATIONS_OPTIONS)}
              {this.renderTextInput('Application fee %', 'applicationFeePercentage', 'col-md-4', 'number')}
            </div>
            {this.renderAvailableAgesEditor()}
          </div>
        </div>
      </div>
    );
  }

  renderRegistrationEdit(): React.ReactNode {
    return (
      <div>
        {this.renderSectionEditActions('registration settings')}
        <div className="card mb-3">
          <div className="card-header">Registration</div>
          <div className="card-body">
            {this.renderCheckbox('Available for registration', 'availableForRegistration')}
            {this.renderCheckbox('Student self registration enabled', 'studentSelfRegistrationEnabled')}
            {this.renderCheckbox('Can accept staff roles', 'canAcceptStaffRoles')}
            {this.renderCheckbox('One-off code on register', 'isOneOffCodeOnRegisterEnabled')}
            {this.renderCheckbox('First admin auto accept', 'isFirstAdminAutoAccept')}
            {this.renderCheckbox('Parents and students auto accept', 'isParentsAndStudentsAutoAccept')}
            {this.renderPermissionPresetEditor()}
          </div>
        </div>
        <div className="card mb-3">
          <div className="card-header">Parent registration child fields</div>
          <div className="card-body">{this.renderParentRegistrationFieldsEditor()}</div>
        </div>
        <div className="card mb-3">
          <div className="card-header">Parent matching</div>
          <div className="card-body">
            {this.renderCheckbox(
              'Can create student from parent permission request',
              'canCreateStudentFromParentPermissionRequest'
            )}
            <div className="form-row">
              {this.renderSelect(
                'Match mode',
                'parentAutoLinkMatchMode',
                PARENT_AUTO_LINK_MATCH_MODE_OPTIONS,
                'col-md-6'
              )}
            </div>
            <div className="form-group">
              <label>
                <strong>Allowed relationships</strong>
              </label>
              <div className="row">
                {ALL_PARENT_RELATIONSHIPS.map(relationship => (
                  <label className="col-md-4 mb-2" key={relationship}>
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={(this.state.form.allowedParentRelationships || []).includes(relationship)}
                      onChange={() => this.onRelationshipToggle(relationship)}
                      disabled={this.state.isSaving}
                    />
                    {relationship}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderInvitesTab(): React.ReactNode {
    const { school } = this.state;
    if (this.state.isEditMode) return this.renderInviteSettingsEdit();

    return (
      <div>
        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-primary" type="button" onClick={this.onEditClick}>
            Edit school
          </button>
        </div>
        <div className="card mb-3">
          <div className="card-header">Invite saving</div>
          <div className="card-body p-0">
            <table className="table table-sm mb-0">
              <tbody>
                {this.renderReadonlyRow(
                  'Event invites',
                  formatValue(getValue(school, ['triggers', 'onEventInviteAction'], ''))
                )}
                {this.renderReadonlyRow(
                  'Tournament invites',
                  formatValue(getValue(school, ['triggers', 'onTournamentInviteAction'], ''))
                )}
                {this.renderReadonlyRow(
                  'Tournament event invites',
                  formatValue(getValue(school, ['triggers', 'onTournamentEventInviteAction'], ''))
                )}
                {this.renderReadonlyRow(
                  'Invite email notification',
                  formatBoolean(propz.get(school, ['triggers', 'onInviteEmailNotification'], false))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  renderInviteSettingsEdit(): React.ReactNode {
    return (
      <div>
        {this.renderSectionEditActions('invite settings')}
        <div className="card mb-3">
          <div className="card-header">Invite settings</div>
          <div className="card-body">
            <div className="form-row">
              {this.renderSelect('Event invites', 'onEventInviteAction', INVITE_ACTION_OPTIONS, 'col-md-4')}
              {this.renderSelect('Tournament invites', 'onTournamentInviteAction', INVITE_ACTION_OPTIONS, 'col-md-4')}
              {this.renderSelect(
                'Tournament event invites',
                'onTournamentEventInviteAction',
                INVITE_ACTION_OPTIONS,
                'col-md-4'
              )}
            </div>
            {this.renderCheckbox('Send email notification on invite', 'onInviteEmailNotification')}
          </div>
        </div>
      </div>
    );
  }

  renderPublicSiteTab(): React.ReactNode {
    const { school } = this.state;
    if (this.state.isEditMode) return this.renderPublicSiteEdit();

    return (
      <div>
        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-primary" type="button" onClick={this.onEditClick}>
            Edit school
          </button>
        </div>
        <div className="card mb-3">
          <div className="card-header">Public site</div>
          <div className="card-body p-0">
            <table className="table table-sm mb-0">
              <tbody>
                {this.renderReadonlyRow('Domain', getValue(school, ['domain']))}
                {this.renderReadonlyRow('Public site URL', getPublicSiteUrl(school))}
                {this.renderReadonlyRow('Can publish website', formatBoolean(school.canPublishWebSite))}
                {this.renderReadonlyRow('Show school select', formatBoolean(school.isShowSchoolSelectOnPublicSite))}
                {this.renderReadonlyRow('Show league table', formatBoolean(school.isShowLeagueTableOnPublicSite))}
                {this.renderReadonlyRow('Show school table', formatBoolean(school.isShowSchoolTableOnPublicSite))}
                {this.renderReadonlyRow('Show tournaments', formatBoolean(school.isShowTournamentsOnPublicSite))}
                {this.renderReadonlyRow(
                  'Show aggregated house league',
                  formatBoolean(school.isShowAggregatedHouseLeagueOnPublicWebsite)
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  renderPublicSiteEdit(): React.ReactNode {
    return (
      <div>
        {this.renderSectionEditActions('public site settings')}
        <div className="card mb-3">
          <div className="card-header">Public site</div>
          <div className="card-body">
            <div className="form-row">{this.renderTextInput('Domain', 'domain', 'col-md-6')}</div>
            {this.renderCheckbox('Can publish website', 'canPublishWebSite')}
            {this.renderCheckbox('Show school select', 'isShowSchoolSelectOnPublicSite')}
            {this.renderCheckbox('Show league table', 'isShowLeagueTableOnPublicSite')}
            {this.renderCheckbox('Show school table', 'isShowSchoolTableOnPublicSite')}
            {this.renderCheckbox('Show tournaments', 'isShowTournamentsOnPublicSite')}
            {this.renderCheckbox('Show aggregated house league', 'isShowAggregatedHouseLeagueOnPublicWebsite')}
          </div>
        </div>
      </div>
    );
  }

  renderFeatureFlagsTab(): React.ReactNode {
    const values = schoolToForm(this.state.school);
    const fields: Array<[string, string]> = [
      ['Chats (school access)', 'eventCommunicationsAvailable'],
      ['Tournament communications (union access)', 'tournamentCommunicationsAvailable'],
      ['Comments', 'settingsCommentsEnabled'],
      ['Photos', 'settingsPhotosEnabled'],
      ['Student availability requests', 'settingsAvailabilityStudentEnabled'],
      ['Parent availability requests', 'settingsAvailabilityParentEnabled'],
      ['Student import for admin', 'studentImportForAdminAllowed'],
      ['Notifications', 'notificationsEnabled'],
      ['Allowed for following', 'allowedForFollowing'],
      ['Parent event performance', 'eventPerformanceEnabledForParent'],
      ['Clubs', 'isClubsEnabled'],
      ['Club priority', 'isClubPriorityEnabled'],
      ['Absence requests', 'isAbsenceRequestEnabled'],
      ['Payments', 'isPaymentsEnabled'],
      ['Favourite sports', 'isFavoriteSportsEnabled'],
      ['Create SMS channel for new users', 'isCreateSmsNotificationChannelForNewUsers'],
      ['Send SMS for all system events', 'isSendSmsWithAllSystemEvents'],
      ['General messages', 'isGeneralMessagesEnabled'],
      ['Coach sees affected events only', 'isCoachSeenOnlyAffectedEvents'],
      ['Teacher sees affected events only', 'isTeacherSeenOnlyAffectedEvents'],
      ['Custom event update message', 'isEventUpdateNotificationCustomMessageEnabled'],
      ['Followers auto-enrol', 'isFollowersAutoenroll'],
      ['Sell photos', 'canSellPhotos'],
      ['Show student event clashes', 'isShowStudentEventClashes']
    ];
    if (this.state.isEditMode) {
      return (
        <div>
          {this.renderSectionEditActions('feature flags')}
          <div className="card mb-3">
            <div className="card-header">Feature flags</div>
            <div className="card-body">
              <div className="row">
                {fields.map(([label, field]) => (
                  <React.Fragment key={field}>{this.renderCheckbox(label, field)}</React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div>
        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-primary" type="button" onClick={this.onEditClick}>
            Edit feature flags
          </button>
        </div>
        <div className="card mb-3">
          <div className="card-header">Feature flags</div>
          <div className="card-body p-0">
            <table className="table table-sm mb-0">
              <tbody>
                {fields.map(([label, field]) => (
                  <React.Fragment key={field}>
                    {this.renderReadonlyRow(label, formatBoolean(values[field]))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
  renderStudents(): React.ReactNode {
    const { schoolId } = this.props.match.params;

    return <SuperAdminSchoolStudents schoolId={schoolId} history={this.props.history} location={this.props.location} />;
  }

  renderForms(): React.ReactNode {
    const { school } = this.state;
    const { schoolId } = this.props.match.params;

    return (
      <SuperAdminSchoolForms
        schoolId={schoolId}
        school={school}
        history={this.props.history}
        location={this.props.location}
      />
    );
  }

  renderHouses(): React.ReactNode {
    const { schoolId } = this.props.match.params;

    return <SuperAdminSchoolHouses schoolId={schoolId} history={this.props.history} location={this.props.location} />;
  }

  renderEvents(): React.ReactNode {
    const { schoolId } = this.props.match.params;

    return <SuperAdminSchoolEvents schoolId={schoolId} history={this.props.history} location={this.props.location} />;
  }

  renderTournaments(): React.ReactNode {
    const { schoolId } = this.props.match.params;

    return (
      <SuperAdminSchoolTournaments schoolId={schoolId} history={this.props.history} location={this.props.location} />
    );
  }

  renderPayments(): React.ReactNode {
    const { schoolId } = this.props.match.params;

    return <SuperAdminSchoolPayments schoolId={schoolId} history={this.props.history} location={this.props.location} />;
  }

  renderSubscriptions(): React.ReactNode {
    const { schoolId } = this.props.match.params;

    return (
      <SuperAdminSchoolSubscriptions schoolId={schoolId} history={this.props.history} location={this.props.location} />
    );
  }

  renderModules(): React.ReactNode {
    const { schoolId } = this.props.match.params;
    return <SuperAdminSchoolModules schoolId={schoolId} />;
  }

  renderSports(): React.ReactNode {
    const schoolId = propz.get(this.state, ['school', 'id'], '');
    return <SuperAdminSchoolSports schoolId={schoolId} />;
  }

  renderAllowedSports(): React.ReactNode {
    const schoolId = propz.get(this.state, ['school', 'id'], '');
    return <SuperAdminSchoolAllowedSports schoolId={schoolId} />;
  }

  renderExports(): React.ReactNode {
    const schoolId = propz.get(this.state, ['school', 'id'], '');
    return <SuperAdminSchoolExports schoolId={schoolId} />;
  }

  renderTemplates(): React.ReactNode {
    const schoolId = propz.get(this.state, ['school', 'id'], '');
    return <SuperAdminSchoolTemplates schoolId={schoolId} />;
  }

  renderUserStats(): React.ReactNode {
    const schoolId = propz.get(this.state, ['school', 'id'], '');
    return <SuperAdminSchoolUserStats schoolId={schoolId} />;
  }

  renderNotifications(): React.ReactNode {
    const { schoolId } = this.props.match.params;

    return <SuperAdminSchoolNotifications schoolId={schoolId} />;
  }

  renderActionDescriptors(): React.ReactNode {
    const { schoolId } = this.props.match.params;

    return (
      <SuperAdminSchoolActionDescriptors
        schoolId={schoolId}
        history={this.props.history}
        location={this.props.location}
      />
    );
  }

  renderGmEmailTemplate(): React.ReactNode {
    const { school, isSaving } = this.state;
    const { schoolId } = this.props.match.params;

    const settings: GmEmailTemplateSettings = {
      gmEmailHeaderColor: propz.get(school, ['gmEmailHeaderColor'], '#1e3a5f'),
      gmEmailHeaderEnabled: propz.get(school, ['gmEmailHeaderEnabled'], true),
      gmEmailHeaderLogoEnabled: propz.get(school, ['gmEmailHeaderLogoEnabled'], true),
      gmEmailHeaderNameEnabled: propz.get(school, ['gmEmailHeaderNameEnabled'], true),
      gmEmailHeaderLogoAlignment: propz.get(school, ['gmEmailHeaderLogoAlignment'], 'left'),
      gmEmailHeaderNamePlacement: propz.get(school, ['gmEmailHeaderNamePlacement'], 'below'),
      gmEmailHeaderNameAlignment: propz.get(school, ['gmEmailHeaderNameAlignment'], 'left'),
      gmEmailFooterHtml: propz.get(school, ['gmEmailFooterHtml'], ''),
      gmEmailFooterBackgroundColor: propz.get(school, ['gmEmailFooterBackgroundColor'], '#f8f9fa'),
      gmEmailFooterTextColor: propz.get(school, ['gmEmailFooterTextColor'], '#64748b'),
      gmEmailFooterAlignment: propz.get(school, ['gmEmailFooterAlignment'], 'left')
    };

    const schoolName = propz.get(school, ['name'], '');
    const schoolLogo =
      propz.get(school, ['pic'], '') || propz.get(school, ['logoUrl'], '') || propz.get(school, ['logo'], '');

    return (
      <div className="p-3">
        <h5 className="mb-4">GM Email Template</h5>
        <p className="text-muted mb-4">These settings control how General Messages emails look when sent.</p>
        <GmEmailTemplateForm
          imageUploader={uploadSuperAdminImage}
          settings={settings}
          schoolName={schoolName}
          schoolLogo={schoolLogo}
          isSaving={isSaving}
          onSave={newSettings => {
            this.setState({ isSaving: true });
            updateSuperAdminSchool(schoolId, newSettings)
              .then(school => {
                this.setState({
                  school,
                  isSaving: false,
                  successMessage: 'GM email template saved.'
                });
              })
              .catch(() => {
                this.setState({ isSaving: false, errorMessage: 'Failed to save.' });
              });
          }}
        />
      </div>
    );
  }

  renderStub(tabName: string): React.ReactNode {
    return (
      <div className="alert alert-info mb-0">
        <strong>{tabName}</strong> will be migrated here as a separate slice.
      </div>
    );
  }

  renderCurrentTab(): React.ReactNode {
    const { currentTabIndex } = this.state;

    switch (currentTabIndex) {
      case 0:
        return this.renderOverview();
      case 1:
        return this.renderSettingsTab();
      case 2:
        return this.renderRegistrationTab();
      case 3:
        return this.renderInvitesTab();
      case 4:
        return this.renderPublicSiteTab();
      case 5:
        return this.renderFeatureFlagsTab();
      case 6:
        return this.renderStudents();
      case 7:
        return this.renderForms();
      case 8:
        return this.renderHouses();
      case 9:
        return this.renderEvents();
      case 10:
        return this.renderTournaments();
      case 11:
        return this.renderPayments();
      case 12:
        return this.renderSubscriptions();
      case 13:
        return this.renderModules();
      case 14:
        return this.renderSports();
      case 15:
        return this.renderAllowedSports();
      case 16:
        return this.renderExports();
      case 17:
        return this.renderTemplates();
      case 18:
        return this.renderUserStats();
      case 19:
        return this.renderNotifications();
      case 20:
        return this.renderActionDescriptors();
      case 21:
        return this.renderGmEmailTemplate();
      case 22:
        return <SuperAdminStudentHistory schoolId={this.props.match.params.schoolId} />;
      default:
        return this.renderStub(TABS[currentTabIndex]);
    }
  }

  render() {
    const { currentTabIndex, errorMessage, successMessage, isLoading, isSaving, school } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <div className="mb-2">
              <Link to="/schools" className="btn btn-sm btn-outline-secondary">
                Back to schools
              </Link>
            </div>
            <h2 className="mb-1">{school ? school.name : 'School sandbox'}</h2>
            <div className="text-muted">
              {school
                ? `${getValue(school, ['kind'], 'School')} · ${formatValue(
                    getValue(school, ['status'], '')
                  )} · ${getPostcode(school)}`
                : 'School details'}
            </div>
          </div>
          <button className="btn btn-outline-secondary" onClick={this.loadSchool} disabled={isSaving}>
            Refresh
          </button>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
        {successMessage && !isLoading && <div className="alert alert-success">{successMessage}</div>}
        {isSaving && <Loader />}

        {school && (
          <GenericSummary tabs={TABS} currentTabIndex={currentTabIndex} onTabClick={this.onTabClick}>
            {this.renderCurrentTab()}
          </GenericSummary>
        )}
      </div>
    );
  }
}
